import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import {
  ArrowRight, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Star,
  TrendingUp, Users, Zap, Calendar, Mail, Lock, ShieldAlert, Clock, Smartphone,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const C = { dark: '#0F0F0F', accent: '#FF2E00', bg: '#0B0B0B', card: '#171717', soft: '#1F1F1F', border: '#2A2A2A' };

/** Public demo landing page rendered at /demo/:slugOrToken. Personalized for one prospect. */
export default function PublicDemo() {
  const { slugOrToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const tracked = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/demo-page/${encodeURIComponent(slugOrToken)}`);
        if (!r.ok) throw new Error(r.status === 404 ? 'not-found' : 'load-failed');
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slugOrToken]);

  // Fire-and-forget view tracker after data loads (once per mount)
  useEffect(() => {
    if (!data || tracked.current) return;
    tracked.current = true;
    fetch(`${API}/api/public/demo-page/${encodeURIComponent(slugOrToken)}/view`, { method: 'POST' }).catch(() => {});
  }, [data, slugOrToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, fontFamily: 'Manrope, sans-serif' }} data-testid="demo-loading">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  if (err === 'not-found' || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: C.bg, fontFamily: 'Manrope, sans-serif' }} data-testid="demo-notfound">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ background: C.accent }}>
          <AlertTriangle className="text-white" size={28} />
        </div>
        <h1 className="text-3xl uppercase tracking-tighter mb-3" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>This demo isn&apos;t live.</h1>
        <p className="text-gray-400 mb-8 max-w-md">The link expired or the slug doesn&apos;t exist. Tap below to build one for your gym.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-widest uppercase text-white" style={{ background: C.accent }} data-testid="demo-cta-home">
          BUILD MY DEMO <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: 'Manrope, sans-serif', color: '#fff' }} data-testid="demo-page">
      <Helmet>
        <title>{`${data.gym_name} · Your HuscleOS Demo`}</title>
        <meta name="description" content={`A personalized HuscleOS preview built for ${data.gym_name}.`} />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <TopBar />
      <Hero data={data} slugOrToken={slugOrToken} />
      <InsightsBlock data={data} />
      <MiniCRM data={data} />
      <FoundingCTA data={data} slugOrToken={slugOrToken} />
      <Footer />
    </div>
  );
}

/* ───────────────────────── Top Bar ────────────────────────── */
function TopBar() {
  return (
    <header className="border-b" style={{ borderColor: C.border, background: 'rgba(11,11,11,0.95)', backdropFilter: 'blur(8px)' }}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1" data-testid="demo-logo">
          <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: '#fff' }}>HUSCLE</span>
          <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: C.accent }}>/OS</span>
        </Link>
        <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-gray-400 hidden sm:block">PRIVATE PREVIEW · NOT INDEXED</span>
      </div>
    </header>
  );
}

/* ───────────────────────── Hero ────────────────────────── */
function Hero({ data, slugOrToken }) {
  const cityState = [data.city, data.state].filter(Boolean).join(', ');
  return (
    <section className="relative overflow-hidden border-b" style={{ borderColor: C.border }}>
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 20% 30%, rgba(255,46,0,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,46,0,0.15), transparent 60%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-24 grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <p className="text-xs font-bold tracking-[0.3em] uppercase mb-4" style={{ color: C.accent }}>BUILT FOR YOU · {cityState || 'YOUR GYM'}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: 'Bebas Neue' }}>
            {data.gym_name},<br/>
            <span style={{ color: C.accent }}>here&apos;s your demo.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-xl leading-relaxed">
            We built a working HuscleOS preview around <strong className="text-white">your gym</strong>. Hit your funnel, your members, your missed follow-ups. Three CTAs below — claim your founding spot, book a call, or have us email you the breakdown.
          </p>
          <div className="flex flex-wrap gap-3 items-center text-xs text-gray-400">
            {data.google_rating != null && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 border" style={{ borderColor: C.border }} data-testid="hero-google-rating">
                <Star size={12} className="text-yellow-400 fill-yellow-400" />
                <strong className="text-white">{data.google_rating}</strong> · {data.review_count || 0} Google reviews
              </span>
            )}
            {data.instagram_followers != null && (
              <a href={data.instagram_url || '#'} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 border hover:border-white transition-colors" style={{ borderColor: C.border }} data-testid="hero-ig-followers">
                <Users size={12} /> <strong className="text-white">{data.instagram_followers.toLocaleString()}</strong> IG followers
              </a>
            )}
            {data.website && (
              <a href={data.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 border hover:border-white transition-colors" style={{ borderColor: C.border }} data-testid="hero-website">
                <ExternalLink size={12} /> Your current site
              </a>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="relative">
            {data.screenshot_url ? (
              <div className="relative aspect-[4/3] border overflow-hidden" style={{ borderColor: C.border, background: C.card }} data-testid="hero-screenshot">
                <img
                  src={data.screenshot_url}
                  alt={`Current site for ${data.gym_name}`}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="absolute top-2 left-2 text-[9px] font-bold tracking-widest uppercase px-2 py-1 text-white" style={{ background: 'rgba(0,0,0,0.7)' }}>YOUR SITE TODAY</div>
              </div>
            ) : (
              <div className="aspect-[4/3] border flex flex-col items-center justify-center p-8 text-center" style={{ borderColor: C.border, background: C.card }} data-testid="hero-no-screenshot">
                <ShieldAlert size={48} className="mb-4" style={{ color: C.accent }} />
                <p className="text-sm text-gray-400">No website listed for {data.gym_name}.</p>
                <p className="text-xs text-gray-500 mt-1">Every prospect Googling you right now → dead end.</p>
              </div>
            )}
            <ScanBadges metrics={data.scan_metrics || {}} />
          </div>
        </div>
      </div>

      {/* Above-the-fold CTAs */}
      <div className="relative max-w-6xl mx-auto px-6 pb-12" data-testid="hero-cta-stack">
        <CTAStack data={data} slugOrToken={slugOrToken} compact />
      </div>
    </section>
  );
}

function ScanBadges({ metrics }) {
  if (!metrics || !metrics.status) return null;
  const items = [
    { key: 'load', icon: <Clock size={11} />, label: 'LOAD', value: metrics.load_time_ms != null ? `${metrics.load_time_ms}ms` : '—', ok: metrics.load_time_ms != null && metrics.load_time_ms < 3000 },
    { key: 'ssl', icon: <Lock size={11} />, label: 'SSL', value: metrics.ssl_valid === true ? 'OK' : metrics.ssl_valid === false ? 'BROKEN' : '—', ok: metrics.ssl_valid === true },
    { key: 'mobile', icon: <Smartphone size={11} />, label: 'MOBILE', value: metrics.mobile_friendly === true ? 'OK' : metrics.mobile_friendly === false ? 'NO' : '—', ok: metrics.mobile_friendly === true },
    { key: 'cta', icon: <Zap size={11} />, label: 'CTA', value: metrics.has_clear_cta === true ? 'OK' : metrics.has_clear_cta === false ? 'MISSING' : '—', ok: metrics.has_clear_cta === true },
  ];
  return (
    <div className="grid grid-cols-4 gap-px mt-3" style={{ background: C.border }} data-testid="hero-scan-badges">
      {items.map((it) => (
        <div key={it.key} className="px-2 py-2" style={{ background: C.bg }}>
          <div className="flex items-center gap-1 text-[9px] font-bold tracking-widest uppercase text-gray-500 mb-0.5">{it.icon} {it.label}</div>
          <div className={`text-xs font-bold ${it.ok ? 'text-emerald-400' : 'text-red-400'}`}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── Insights ────────────────────────── */
function InsightsBlock({ data }) {
  const insights = data.ai_insights || [];
  if (insights.length === 0) return null;
  return (
    <section className="border-b py-16 sm:py-20" style={{ borderColor: C.border }} data-testid="demo-insights">
      <div className="max-w-6xl mx-auto px-6">
        <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: C.accent }}>WE LOOKED AT YOUR FUNNEL</p>
        <h2 className="text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.95] mb-10 max-w-3xl" style={{ fontFamily: 'Bebas Neue' }}>
          3 things losing you members <span style={{ color: C.accent }}>right now.</span>
        </h2>
        <div className="grid sm:grid-cols-3 gap-px" style={{ background: C.border }}>
          {insights.map((text, i) => (
            <div key={i} className="p-6 sm:p-7" style={{ background: C.card }} data-testid={`insight-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-4xl font-black tabular-nums" style={{ fontFamily: 'Bebas Neue', color: C.accent }}>0{i + 1}</span>
                <AlertTriangle size={18} className="text-gray-500" />
              </div>
              <p className="text-sm text-gray-200 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Mini-CRM Preview ────────────────────────── */
function MiniCRM({ data }) {
  const stats = data.preview?.stats || {};
  const leads = data.preview?.leads || [];
  const cards = [
    { label: 'NEW LEADS', value: stats.new_leads_this_week, icon: <TrendingUp size={14} /> },
    { label: 'ACTIVE MEMBERS', value: stats.active_members, icon: <Users size={14} /> },
    { label: 'EST. MRR', value: stats.mrr, icon: <Zap size={14} /> },
    { label: 'DRIPS RUNNING', value: stats.drips_active, icon: <Zap size={14} /> },
  ];
  return (
    <section className="py-16 sm:py-24 border-b" style={{ borderColor: C.border, background: C.dark }} data-testid="demo-mini-crm">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: C.accent }}>INSIDE YOUR HUSCLEOS</p>
            <h2 className="text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.95] max-w-2xl" style={{ fontFamily: 'Bebas Neue' }}>
              How <span style={{ color: C.accent }}>{data.gym_name}</span> would run.
            </h2>
          </div>
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border self-start" style={{ borderColor: C.border, color: '#9CA3AF' }}>READ-ONLY PREVIEW</span>
        </div>

        {/* Mock browser chrome */}
        <div className="border" style={{ borderColor: C.border, background: C.card }}>
          <div className="flex items-center gap-1.5 px-4 py-3 border-b" style={{ borderColor: C.border }}>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="ml-3 text-[10px] tracking-widest uppercase text-gray-500">huscleos.com/dashboard</span>
          </div>

          {/* Stat row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px" style={{ background: C.border }}>
            {cards.map((s) => (
              <div key={s.label} className="px-4 py-5" style={{ background: C.card }} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest uppercase text-gray-500 mb-2">{s.icon} {s.label}</div>
                <div className="text-2xl font-black tabular-nums leading-none" style={{ fontFamily: 'Manrope', color: '#fff' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Sample lead table */}
          <div className="p-4 sm:p-6 border-t" style={{ borderColor: C.border }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">LIVE PIPELINE · {leads.length} ACTIVE</p>
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-600">SHOW RATE {stats.show_rate} · NO-SHOWS RECOVERED {stats.no_show_recovered}</p>
            </div>
            <div className="border" style={{ borderColor: C.border }}>
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[9px] font-bold tracking-widest uppercase text-gray-500 border-b" style={{ borderColor: C.border, background: C.soft }}>
                <span className="col-span-4">LEAD</span>
                <span className="col-span-4">STAGE</span>
                <span className="col-span-2">VALUE</span>
                <span className="col-span-2 text-right">LAST TOUCH</span>
              </div>
              {leads.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-3 text-xs border-b last:border-b-0 items-center" style={{ borderColor: C.border }} data-testid={`preview-lead-${i}`}>
                  <span className="col-span-4 text-white font-semibold">{l.name}</span>
                  <span className="col-span-4 text-gray-300">{l.stage}</span>
                  <span className="col-span-2 tabular-nums text-gray-400">{l.value}</span>
                  <span className="col-span-2 text-right tabular-nums text-gray-500">{l.last_touch_hours}h ago</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-3 italic">Mock data tied to {data.gym_name}. Real data flows in once you go live.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Founding CTA + Email Capture ────────────────────────── */
function FoundingCTA({ data, slugOrToken }) {
  return (
    <section className="py-20 sm:py-28 border-b" style={{ borderColor: C.border, background: C.bg }} data-testid="demo-founding-cta">
      <div className="max-w-6xl mx-auto px-6">
        <p className="text-xs font-bold tracking-[0.3em] uppercase mb-4 text-center" style={{ color: C.accent }}>3 WAYS FORWARD</p>
        <h2 className="text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.95] mb-12 text-center max-w-3xl mx-auto" style={{ fontFamily: 'Bebas Neue' }}>
          Pick the move that <span style={{ color: C.accent }}>fits your gym.</span>
        </h2>
        <CTAStack data={data} slugOrToken={slugOrToken} />
      </div>
    </section>
  );
}

function CTAStack({ data, slugOrToken, compact = false }) {
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const startCheckout = useCallback(async () => {
    setLoadingCheckout(true);
    try {
      const r = await fetch(`${API}/api/huscleos/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: 'launch',
          origin_url: window.location.origin,
          gym_name: data.gym_name,
        }),
      });
      const json = await r.json();
      if (json.url) {
        window.location.href = json.url;
      } else {
        toast.error('Checkout unavailable — please book a call instead.');
      }
    } catch {
      toast.error('Checkout error');
    } finally {
      setLoadingCheckout(false);
    }
  }, [data.gym_name]);

  return (
    <div className={`grid ${compact ? 'sm:grid-cols-3' : 'md:grid-cols-3'} gap-3`}>
      <button
        onClick={startCheckout}
        disabled={loadingCheckout}
        className="flex flex-col items-start p-5 sm:p-6 text-left transition-all hover:translate-y-[-2px] disabled:opacity-60"
        style={{ background: C.accent, color: '#fff' }}
        data-testid="cta-stripe-checkout"
      >
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2 opacity-80">FASTEST · NO CALL</span>
        <span className="text-2xl uppercase tracking-tight font-black leading-tight mb-1" style={{ fontFamily: 'Bebas Neue' }}>Claim founding spot</span>
        <span className="text-sm opacity-90 mb-3">${data.founding_price_usd || 247}/mo locked for life. Setup in 24h.</span>
        <span className="text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1.5">{loadingCheckout ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />} START NOW</span>
      </button>

      <a
        href={data.calendly_url || 'https://cal.com/huscleos/15min'}
        target="_blank"
        rel="noreferrer"
        className="flex flex-col items-start p-5 sm:p-6 text-left transition-all hover:translate-y-[-2px] border"
        style={{ background: C.card, borderColor: C.border, color: '#fff' }}
        data-testid="cta-book-call"
      >
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2 text-gray-400">15 MIN · TALK FIRST</span>
        <span className="text-2xl uppercase tracking-tight font-black leading-tight mb-1" style={{ fontFamily: 'Bebas Neue' }}>Book a walk-through</span>
        <span className="text-sm text-gray-300 mb-3">We&apos;ll screen-share your real funnel + answer everything.</span>
        <span className="text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1.5 text-white"><Calendar size={12} /> PICK A TIME</span>
      </a>

      <EmailCaptureCard slugOrToken={slugOrToken} gymName={data.gym_name} />
    </div>
  );
}

function EmailCaptureCard({ slugOrToken, gymName }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Drop a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/public/demo-page/${encodeURIComponent(slugOrToken)}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
      if (!r.ok) throw new Error('failed');
      setDone(true);
      toast.success('Sent — check your inbox.');
    } catch {
      toast.error('Capture failed — try again or book a call.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-start p-5 sm:p-6 border" style={{ background: C.card, borderColor: C.border, color: '#fff' }} data-testid="cta-capture-done">
        <CheckCircle2 size={28} className="mb-3 text-emerald-400" />
        <span className="text-2xl uppercase tracking-tight font-black leading-tight mb-2" style={{ fontFamily: 'Bebas Neue' }}>You&apos;re in.</span>
        <span className="text-sm text-gray-300">We just sent the breakdown to your inbox for {gymName}. Reply if you have questions.</span>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col items-start p-5 sm:p-6 border"
      style={{ background: C.card, borderColor: C.border, color: '#fff' }}
      data-testid="cta-email-capture"
    >
      <span className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2 text-gray-400">NOT READY · NO PRESSURE</span>
      <span className="text-2xl uppercase tracking-tight font-black leading-tight mb-1" style={{ fontFamily: 'Bebas Neue' }}>Email me the breakdown</span>
      <span className="text-sm text-gray-300 mb-4">Get the funnel audit + demo link to forward to your team.</span>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        className="w-full px-3 py-2 mb-2 text-sm bg-black/40 border outline-none focus:border-white"
        style={{ borderColor: C.border, color: '#fff' }}
        data-testid="capture-name"
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full px-3 py-2 mb-3 text-sm bg-black/40 border outline-none focus:border-white"
        style={{ borderColor: C.border, color: '#fff' }}
        data-testid="capture-email"
      />
      <button
        type="submit"
        disabled={submitting}
        className="text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1.5 text-white border border-white px-3 py-2 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
        data-testid="capture-submit"
      >
        {submitting ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} SEND IT
      </button>
    </form>
  );
}

/* ───────────────────────── Footer ────────────────────────── */
function Footer() {
  return (
    <footer className="py-10 text-center" style={{ background: C.bg }}>
      <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-gray-500">
        HUSCLE/OS · BUILT BY OPERATORS · NOT MARKETERS
      </p>
      <p className="text-[10px] text-gray-700 mt-2">
        This page is unlisted. <Link to="/" className="hover:text-white">huscleos.com</Link>
      </p>
    </footer>
  );
}
